import { debug, george, NetbankingAPI, observer, pr, ui } from "aaa";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { STYLE_CENTERED_CARD } from "components/util/primitives";
import { compact } from "lodash";
import * as moment from "moment";
import { NetbankingAPIClient } from "network/APIClient";
import * as React from "react";
import externalInterfaceStore, { IDeeplinkState } from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { IEvaluation, IEvaluationAction, IGetEvaluationsResponseWithEvaluations, IGetEvaluationsResponseWithoutEvaluations } from "types/allnetServices";
import { openAsLink, safeWindowOpen } from "util/helpers";
import { AssetCalculationStore } from "./AssetCalculationStore";

const isResponseWithEvaluations = (
    evaluationsResponse?: IGetEvaluationsResponseWithEvaluations | IGetEvaluationsResponseWithoutEvaluations
): evaluationsResponse is IGetEvaluationsResponseWithEvaluations => {
    return ((evaluationsResponse as IGetEvaluationsResponseWithEvaluations).evaluations) !== undefined;
};

const isResponseWithoutEvaluations = (
    evaluationsResponse?: IGetEvaluationsResponseWithEvaluations | IGetEvaluationsResponseWithoutEvaluations
): evaluationsResponse is IGetEvaluationsResponseWithoutEvaluations => {
    return ((evaluationsResponse as IGetEvaluationsResponseWithoutEvaluations).actions) !== undefined;
};

const isResponseWithSingleEvaluation = (
    evaluationsResponse?: IGetEvaluationsResponseWithEvaluations | IGetEvaluationsResponseWithoutEvaluations
): evaluationsResponse is IGetEvaluationsResponseWithEvaluations => {
    return isResponseWithoutEvaluations(evaluationsResponse) && !!evaluationsResponse.guid;
}

const Evaluation = ({ onClick, evaluation, style, isMobile }:
    { onClick: () => void; evaluation: IEvaluation; style?: React.CSSProperties, isMobile?: boolean }
) => {
    const [title, address] = evaluation.description.split("\n");
    const dateString = moment(evaluation.evaluationDate).format("DD.MM.YYYY");

    return (
        <george.Card
            disableTopSpacing
            style={{
                borderRadius: 8,
                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)",
                padding: 32,
                textAlign: "center",
                display: "flex",
                gap: 24,
                flexDirection: isMobile ? "column" : "row",
                ...STYLE_CENTERED_CARD,
                ...style
            }}
        >
            <div style={{ textAlign: isMobile ? "center" : "left", flex: 1 }}>
                <pr.P_18_TITLE style={{ color: ui.isDarkMode ? "white" : "#0A285C", fontWeight: 600 }}>
                    {title}
                </pr.P_18_TITLE>
                <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 8 }}>
                    {address}
                </pr.P_16_CONTENT>
                <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C" }}>
                    {dateString}
                </pr.P_16_CONTENT>
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
            >
                <GdsFakeActionButton color="primary" onClick={onClick}>
                    {ui.__("common.edit")}
                </GdsFakeActionButton>
            </div>
        </george.Card>
    );
}

export const AssetCalculationOverview = observer(({ store }: { store: AssetCalculationStore }) => {

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;

    const handleClickCalculation = async (actions: IEvaluationAction[], evaluationId?: string) => {
        try {
            const calculateAction = actions.find(action => action.name === "CALCULATE");

            if (!calculateAction) {
                return;
            }

            const clientId = externalInterfaceStore.isGeorgeApp
                ? calculateAction.tokenHandover.targetClientIdApp
                : calculateAction.tokenHandover.targetClientIdWeb;

            const target = NetbankingAPI.tokenExchange(clientId);
            // TODO - type response of NetbankingAPI.tokenExchange and use requestType instead of requestJSON everywhere were it's used like this
            const res: any = await NetbankingAPIClient.requestJSON(target);
            const accessToken = res.access_token;

            const stateObject: IDeeplinkState = {
                returnState: "handover/success/store"
            };

            const returnStateParam = `state=${btoa(JSON.stringify(stateObject))}`;
            const productIdParam = externalInterfaceStore.productId ? `productId=${externalInterfaceStore.productId}` : undefined;
            const evaluationIdParam = evaluationId ? `evaluationId=${evaluationId}` : undefined;
            const params = compact([returnStateParam, productIdParam, evaluationIdParam]).join("&");

            let handoverUrl = calculateAction._links.action.href;

            if (handoverUrl.includes("?")) {
                handoverUrl = `${handoverUrl}&${params}#access_token=${accessToken}`;
            } else {
                handoverUrl = `${handoverUrl}?${params}#access_token=${accessToken}`;
            }

            debug.log("### handoverUrl", handoverUrl);

            // openAsLink() Doesn't work in iOS 17 -> for now use safeWindowOpen
            safeWindowOpen(handoverUrl);
            // openAsLink(
            //     externalInterfaceStore.isGeorgeApp
            //         // TODO remove georgeapp link? - ask for native app test
            //         ? `georgeapp://at/open_external_link?url=${encodeURIComponent(handoverUrl)}`
            //         : handoverUrl,
            //     "_blank"
            // );
        } catch (error) {
            pushRoute("/error");
        }
    };

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;
    // TODO use if floating action button is wanted
    const PageComponent = externalInterfaceStore.isGeorgeApp ? george.PageWithActionButton : React.Fragment;

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const singleEvaluation = store.evaluationsResponse as (IEvaluation | undefined);

    const hasAssets = (store.evaluationsResponse?.evaluations?.length ?? 0) > 0 || isResponseWithSingleEvaluation(store.evaluationsResponse);

    return (
        <BackgroundComponent style={{ boxSizing: "border-box", paddingTop: 48, paddingBottom: 16 }}>
            <george.Card
                disableTopSpacing
                style={{
                    borderRadius: 8,
                    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)",
                    padding: 32,
                    marginBottom: 32,
                }}
            >
                <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                    <div>
                        <img
                            src={require("../../assets/asset-calculation.png")}
                            alt="icon"
                        />
                    </div>
                    <div>
                        <pr.P_18_TITLE style={{ fontWeight: 700, color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            {ui.__("assetCalculation.info.title")}
                        </pr.P_18_TITLE>
                        <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            {ui.__("assetCalculation.info.text")}
                        </pr.P_16_CONTENT>
                    </div>
                </div>
            </george.Card>
            {hasAssets && (
                <div style={{ ...pr.STYLE_CENTERED_CARD, marginBottom: 16 }}>
                    <pr.P_22_TITLE style={{ fontWeight: 700, color: ui.isDarkMode ? "white" : "#0A285C" }}>
                        {ui.__("assetCalculation.evaluations.title")}
                    </pr.P_22_TITLE>
                </div>
            )}
            {
                store.evaluationsResponse?.evaluations?.filter(evaluation => evaluation.actions.some(action => action.name === "CALCULATE"))
                    .sort((a, b) => new Date(b.evaluationDate).getTime() - new Date(a.evaluationDate).getTime()) // sort by date descending
                    .map((evaluation, index) => (
                        <Evaluation
                            key={evaluation.guid}
                            onClick={() => handleClickCalculation(evaluation.actions, evaluation.guid)}
                            style={{ marginTop: index > 0 ? 16 : undefined }}
                            evaluation={evaluation}
                            isMobile={isMobile}
                        />
                    ))
            }
            {
                isResponseWithSingleEvaluation(store.evaluationsResponse) && singleEvaluation && (
                    <Evaluation
                        onClick={() => handleClickCalculation(singleEvaluation?.actions ?? [], singleEvaluation?.guid)}
                        evaluation={singleEvaluation}
                        isMobile={isMobile}
                    />
                )
            }
            <div style={{ marginTop: hasAssets ? 32 : undefined, ...STYLE_CENTERED_CARD }}>
                {/*
                    TODO: use Regular action button for floating button if wanted - see TPPList.tsx for usage with automatic floating/inline.
                    The nesting is important otherwise the button might be off
                */}
                {isResponseWithEvaluations(store.evaluationsResponse) && (
                    <GdsFakeActionButton color="primary" onClick={() => handleClickCalculation(store.evaluationsResponse?.evaluations?.[0].actions ?? [])}>
                        {hasAssets && ui.__("assetCalculation.button.calculateAnotherAsset")}
                        {!hasAssets && ui.__("assetCalculation.button.calculateNewAsset")}
                    </GdsFakeActionButton>
                )}
                {isResponseWithoutEvaluations(store.evaluationsResponse) && (
                    <GdsFakeActionButton color="primary" onClick={() => handleClickCalculation(store.evaluationsResponse?.actions ?? [])}>
                        {hasAssets && ui.__("assetCalculation.button.calculateAnotherAsset")}
                        {!hasAssets && ui.__("assetCalculation.button.calculateNewAsset")}
                    </GdsFakeActionButton>
                )}
            </div>
        </BackgroundComponent>
    );
});



// WEBPACK FOOTER //
// ./src/components/assetCalculation/AssetCalculationOverview.tsx